import { Exercise as ComponentExercise, ExerciseType, ComprehensionQuestionData } from "@/services/admin/exercises.interfaces";
import { EditorInterface } from "./EditorInterface";
import { BookOpen } from "lucide-react";
import { parseExerciseData } from "./utils";

/**
 * Editor para exercícios de compreensão (versão unificada)
 * Combina as funcionalidades de CardInfo para questões de compreensão
 */
export class ComprehensionQuestionInfo extends EditorInterface {
  /**
   * Retorna as informações para exibir no cartão do exercício de compreensão
   * @param exercise Objeto do exercício
   */
  static GetCardInfo(exercise: ComponentExercise): {
    title: string;
    subtitle: string;
    description?: string;
  } {
    if (!exercise || (exercise.type !== ExerciseType.ComprehensionQuestion && !ComprehensionQuestionInfo.SupportsExerciseType(exercise.type as unknown as string))) {
      return {
        title: 'Questão de Compreensão',
        subtitle: 'Sem detalhes',
      };
    }

    try {
      // Usar parseExerciseData para garantir que data seja parseado se for string JSON
      const parsedExercise = parseExerciseData(exercise);
      
      // Extrair os dados do exercício
      let question = 'Sem pergunta';
      let answer = 'Sem resposta';
      
      // Após parseExerciseData, data sempre será um objeto (ou undefined)
      if (parsedExercise.data && typeof parsedExercise.data === 'object') {
        const data = parsedExercise.data as ComprehensionQuestionData;
        question = data.question_text || 'Sem pergunta';
        answer = data.answer || 'Sem resposta';
      }
      
      // Cria prévia da resposta
      const answerPreview = answer.length > 30 ? `${answer.substring(0, 30)}...` : answer;
      
      return {
        title: "Questão de Compreensão",
        subtitle: question.length > 60 ? `${question.substring(0, 60)}...` : question,
        description: `Resposta: ${answerPreview}`,
      };
    } catch (error) {
      console.error("Erro ao processar dados do exercício de compreensão:", error);
      return {
        title: 'Questão de Compreensão',
        subtitle: 'Erro ao processar dados',
      };
    }
  }
  
  /**
   * Retorna o título do tipo de exercício de compreensão
   */
  static getTitle(): string {
    return "Questão de Compreensão";
  }
  
  /**
   * Retorna uma descrição formatada para o exercício de compreensão
   * @param exercise Objeto do exercício
   */
  static getDescription(exercise: ComponentExercise): string {
    if (!exercise) {
      return "Sem detalhes";
    }
    
    try {
      // Usar parseExerciseData para garantir que data seja parseado se for string JSON
      const parsedExercise = parseExerciseData(exercise);
      
      // Extrair a resposta
      let answer = 'Sem resposta';
      
      // Após parseExerciseData, data sempre será um objeto (ou undefined)
      if (parsedExercise.data && typeof parsedExercise.data === 'object') {
        const data = parsedExercise.data as ComprehensionQuestionData;
        answer = data.answer || 'Sem resposta';
      }
      
      return answer.length > 0 ? "Tem resposta definida" : "Sem resposta definida";
    } catch (error) {
      console.error("Erro ao processar dados do exercício:", error);
      return "Erro ao processar dados";
    }
  }
  
  /**
   * Retorna o ícone para exercícios de compreensão
   */
  static getIcon() {
    return BookOpen;
  }
  
  /**
   * Verifica se o exercício é do tipo de questão de compreensão
   */
  static SupportsExerciseType(type: string): boolean {
    return type === ExerciseType.ComprehensionQuestion;
  }
}